import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { CashButton, CashDialog, CashFacts, CashField, CashNotice } from './CashPrimitives';
import { CashPeriodPicker, cashPeriodValid } from './CashPeriodPicker';
import { cashAmountInput, formatCashMinor, parseCashAmount } from './cashMoney';
const labels = {
    ru: {
        futureTotal: 'Предоплата будущих периодов',
        prior: 'Долг прошлых периодов',
        accrued: 'Начислено за месяц',
        allocated: 'Оплачено / зачтено за месяц',
        remaining: 'Осталось за месяц',
        outstanding: 'Осталось оплатить',
        forecast: 'Прогноз',
        cash: 'Фактически выплачено в месяце',
        advance: 'Незачтённый целевой аванс',
        asOf: 'Состояние на',
        incomplete: 'Показана только доступная часть выплаты',
        unknown: 'Черновик: история не подтверждена; его сумма не включена в долг',
        pay: 'Оплатить',
        details: 'Подробности',
        known: 'Известный остаток',
        paid: 'Оплачено',
        all: 'Все месяцы',
        futureOnly: 'Аванс будущего месяца',
        salaryAdvance: 'Аванс за',
        finalize: 'Уточнить сумму',
        noDebt: 'Долга нет',
        periods: 'Распределение по периодам',
        amount: 'Сумма',
        future: 'Добавить аванс будущего месяца',
        month: 'Целевой месяц',
        approval: 'Будущий аванс требует отдельного независимого одобрения по действующим правам',
        total: 'Всего к выдаче',
        next: 'Продолжить к выплате',
        cancel: 'Отмена',
        due: 'Срок оплаты',
        noDue: 'Срок не задан',
        overdue: 'Просрочено',
        retarget: 'Переназначить остаток',
        reason: 'Причина',
        save: 'Сохранить назначение',
        changed: 'Выбранные периоды стали недоступны. Закройте форму и обновите расчёт.',
    },
    az: {
        futureTotal: 'Gələcək dövrlər üçün əvvəlcədən ödəniş',
        prior: 'Əvvəlki dövrlərin borcu',
        accrued: 'Ay üzrə hesablanıb',
        allocated: 'Ay üzrə ödənilib / əvəzləşdirilib',
        remaining: 'Ay üzrə qalıq',
        outstanding: 'Ödəniləcək qalıq',
        forecast: 'Proqnoz',
        cash: 'Ay ərzində faktiki ödənilib',
        advance: 'Əvəzləşdirilməmiş məqsədli avans',
        asOf: 'Vəziyyət tarixi',
        incomplete: 'Ödənişin yalnız əlçatan hissəsi göstərilir',
        unknown: 'Qaralama: tarixçə təsdiqlənməyib; məbləği borca daxil deyil',
        pay: 'Ödə',
        details: 'Ətraflı',
        known: 'Məlum qalıq',
        paid: 'Ödənilib',
        all: 'Bütün aylar',
        futureOnly: 'Gələcək ay üçün avans',
        salaryAdvance: 'Ay üzrə avans',
        finalize: 'Məbləği dəqiqləşdir',
        noDebt: 'Borc yoxdur',
        periods: 'Dövrlər üzrə bölgü',
        amount: 'Məbləğ',
        future: 'Gələcək ay üçün avans əlavə et',
        month: 'Təyinat ayı',
        approval: 'Gələcək avans qüvvədə olan səlahiyyətlərlə müstəqil təsdiq tələb edir',
        total: 'Cəmi veriləcək',
        next: 'Ödənişə davam et',
        cancel: 'Ləğv et',
        due: 'Ödəniş tarixi',
        noDue: 'Müddət təyin edilməyib',
        overdue: 'Gecikib',
        retarget: 'Qalığın təyinatını dəyiş',
        reason: 'Səbəb',
        save: 'Təyinatı saxla',
        changed: 'Seçilmiş dövrlər artıq əlçatan deyil. Formanı bağlayıb hesablaşmanı yeniləyin.',
    },
    en: {
        futureTotal: 'Prepaid future periods',
        prior: 'Debt from earlier periods',
        accrued: 'Accrued for the month',
        allocated: 'Paid / allocated to the month',
        remaining: 'Remaining for the month',
        outstanding: 'Outstanding',
        forecast: 'Forecast',
        cash: 'Cash paid during the month',
        advance: 'Unallocated targeted advance',
        asOf: 'State as of',
        incomplete: 'Only the permitted part of the payment is shown',
        unknown: 'Draft: history is not confirmed; its amount is excluded from debt',
        pay: 'Pay',
        details: 'Details',
        known: 'Known remaining amount',
        paid: 'Paid',
        all: 'All months',
        futureOnly: 'Advance for a future month',
        salaryAdvance: 'Advance for',
        finalize: 'Set final amount',
        noDebt: 'No debt',
        periods: 'Allocation by period',
        amount: 'Amount',
        future: 'Add an advance for a future month',
        month: 'Target month',
        approval: 'A future advance requires independent approval under the existing permissions',
        total: 'Total cash payment',
        next: 'Continue to payment',
        cancel: 'Cancel',
        due: 'Payment due',
        noDue: 'No due date',
        overdue: 'Overdue',
        retarget: 'Reassign the unused balance',
        reason: 'Reason',
        save: 'Save assignment',
        changed: 'Selected periods are no longer available. Close the form and refresh the calculation.',
    },
};
function cardPeriods(card) {
    return card.periods.filter(p => (card.periodMode === 'ALL' || !card.month || p.month <= card.month) &&
        BigInt(p.remainingMinor) > 0n);
}
function paymentContext(card) {
    const periods = cardPeriods(card);
    return {
        title: card.title,
        storeId: card.storeId,
        storeName: card.storeName,
        month: card.periodMode === 'ALL'
            ? periods
                .map(p => p.month)
                .filter((m, i, a) => a.indexOf(m) === i)
                .join(', ')
            : card.month,
        remainingMinor: card.totalDueMinor ?? (BigInt(card.priorDebtMinor) + BigInt(card.remainingMinor)).toString(),
        purpose: [
            card.title,
            periods
                .map(p => p.month)
                .filter((m, i, a) => a.indexOf(m) === i)
                .join(', '),
        ]
            .filter(Boolean)
            .join(' · '),
        kind: card.employeeId ? 'SALARY' : 'GENERAL',
    };
}
export function CashRecipientCards(p) {
    const t = labels[p.locale], [selected, setSelected] = useState(null);
    const lastRecovered = useRef(p.recovered);
    useEffect(() => {
        if (p.recovered && p.recovered !== lastRecovered.current) {
            lastRecovered.current = p.recovered;
            if (selected?.kind === 'retarget' &&
                p.recovered.type === 'TARGETED_ADVANCE_RETARGET' &&
                p.recovered.result.status === 'COMPLETED') {
                setSelected(null);
                p.onDirty(false);
            }
        }
    }, [p.recovered, selected?.kind, p.onDirty]);
    const [localExpanded, setExpanded] = useState([]);
    const expanded = p.expandedKeys ?? localExpanded;
    useEffect(() => {
        if (p.futureCardKey)
            setSelected({ key: p.futureCardKey, kind: 'future' });
    }, [p.futureCardKey]);
    const card = p.cards.find(c => c.key === selected?.key), advance = card?.advances.find(a => a.advanceId === selected?.advanceId && a.month === selected?.month);
    const allowed = card &&
        (selected?.kind === 'pay'
            ? card.canPay && cardPeriods(card).length > 0
            : selected?.kind === 'future'
                ? card.canAdvance
                : advance?.canRetarget && BigInt(advance.availableMinor) > 0n);
    useEffect(() => {
        if (selected && !allowed) {
            setSelected(null);
            p.onDirty(false);
        }
    }, [selected, allowed, p.onDirty]);
    const close = () => p.guardedChange(() => {
        setSelected(null);
        p.onFutureClose?.();
        p.onDirty(false);
    });
    const pay = (c) => {
        const periods = cardPeriods(c);
        if (periods.length === 1)
            p.onPay({ mode: 'BASIS', basisId: periods[0].basisId }, c.categoryId, c.currency, {
                ...paymentContext(c),
                month: periods[0].month,
                remainingMinor: periods[0].remainingMinor,
            });
        else
            setSelected({ key: c.key, kind: 'pay' });
    };
    return (_jsxs("div", { className: "cash-recipient-list", children: [!p.hideList &&
                p.cards.map(c => {
                    const money = (n) => formatCashMinor(n, c.currency, p.locale), periods = cardPeriods(c), total = c.totalDueMinor ?? (BigInt(c.priorDebtMinor) + BigInt(c.remainingMinor)).toString(), forecasts = (p.bases ?? []).filter(b => b.forecast &&
                        (c.employeeId
                            ? b.employeeId === c.employeeId
                            : b.recurringRuleId === c.recurringRuleId) &&
                        b.storeId === c.storeId &&
                        (b.accountId ?? '') === (c.accountId ?? '') &&
                        b.currency === c.currency &&
                        (c.periodMode === 'ALL' || !c.month || b.month === c.month)), forecast = p.status === 'forecast', forecastCount = c.forecastCount ?? forecasts.length, isOpen = expanded.includes(c.key);
                    return (_jsxs("article", { className: "cash-card cash-recipient-row", children: [_jsxs("div", { className: "cash-recipient-heading", children: [_jsxs("div", { children: [_jsx("h3", { children: c.title }), _jsx("p", { className: "cash-muted", children: [c.storeName, c.periodMode === 'ALL' || !c.month ? t.all : c.month]
                                                    .filter(Boolean)
                                                    .join(' · ') })] }), _jsxs("div", { className: "cash-recipient-amount", children: [_jsx("small", { children: forecast
                                                    ? t.forecast
                                                    : c.unknownHistoryCount
                                                        ? t.known
                                                        : BigInt(total) > 0n
                                                            ? t.outstanding
                                                            : t.noDebt }), _jsx("strong", { children: !forecast && c.unknownHistoryCount > 0 && total === '0'
                                                    ? { az: 'Məlum deyil', ru: 'Неизвестно', en: 'Unknown' }[p.locale]
                                                    : money(forecast ? c.forecastMinor : total) }), !forecast &&
                                                c.unknownHistoryCount === 0 &&
                                                total === '0' &&
                                                (c.paidDocumentCount ?? 0) > 0 && _jsx("span", { className: "cash-badge", children: t.paid })] })] }), c.unknownHistoryCount > 0 && !forecast && (_jsx(CashNotice, { tone: "warn", children: t.unknown })), _jsxs("div", { className: "cash-actions cash-recipient-actions", children: [!forecast && c.canPay && periods.length > 0 && (_jsx(CashButton, { primary: true, disabled: p.busy, onClick: () => pay(c), children: t.pay })), forecast &&
                                        forecasts.length === 1 &&
                                        forecastCount === 1 &&
                                        forecasts[0].actions.includes('RECURRING_FORECAST_FINALIZE') &&
                                        p.onFinalize && (_jsx(CashButton, { primary: true, disabled: p.busy, onClick: () => p.onFinalize?.(forecasts[0].id), children: t.finalize })), _jsx(CashButton, { "aria-expanded": isOpen, "aria-controls": `cash-recipient-${c.key}`, onClick: () => {
                                            const keys = isOpen ? expanded.filter(k => k !== c.key) : [...expanded, c.key];
                                            setExpanded(keys);
                                            p.onExpandedChange?.(keys);
                                        }, children: t.details })] }), forecast && forecasts.length < forecastCount && (_jsxs(CashNotice, { children: [{
                                        az: 'Bu alan üzrə bütün proqnozlar hələ yüklənməyib. Davam etmək üçün sənədləri yükləyin.',
                                        ru: 'Прогнозы этого получателя загружены не полностью. Загрузите документы, чтобы выбрать сумму для уточнения.',
                                        en: 'Forecast documents for this recipient are not fully loaded. Load them to choose an amount to finalize.',
                                    }[p.locale], _jsx(CashButton, { disabled: p.busy, onClick: () => void (p.onLoadMore ?? p.onReload)(), children: { az: 'Sənədləri yüklə', ru: 'Загрузить документы', en: 'Load documents' }[p.locale] })] })), _jsxs("div", { id: `cash-recipient-${c.key}`, hidden: !isOpen, className: "cash-recipient-details", children: [!forecast && (_jsx(CashFacts, { rows: [
                                            ...(c.periodMode !== 'ALL' && c.month
                                                ? [
                                                    [t.prior, money(c.priorDebtMinor)],
                                                    [`${t.accrued} · ${c.month}`, money(c.accruedMinor)],
                                                    [`${t.allocated} · ${c.month}`, money(c.allocatedMinor)],
                                                    [`${t.remaining} · ${c.month}`, money(c.remainingMinor)],
                                                ]
                                                : []),
                                            ...(c.periodMode !== 'ALL' && c.month
                                                ? [[t.futureTotal, money(c.futureAdvanceMinor)]]
                                                : []),
                                            ...(c.periodMode !== 'ALL' && c.month
                                                ? [[`${t.cash} · ${c.month}`, money(c.cashPaidMinor)]]
                                                : []),
                                            [t.asOf, new Date(c.asOf).toLocaleString(p.locale)],
                                        ] })), c.autoFromMonth && c.month && c.month < c.autoFromMonth && (_jsxs(CashNotice, { children: [{
                                                az: 'Avtomatik hesablanma bu aydan başlayır; əvvəlki tarixçəni ayrıca yoxlayın',
                                                ru: 'Автоматические начисления начинаются с этого месяца; более раннюю историю проверьте отдельно',
                                                en: 'Automatic accruals start in this month; review earlier history individually',
                                            }[p.locale], ": ", c.autoFromMonth] })), c.cashPaidIncomplete && _jsx(CashNotice, { children: t.incomplete }), !forecast &&
                                        c.confirmedPeriods &&
                                        c.confirmedPeriods.map(a => (_jsxs("div", { className: "cash-section", children: [_jsxs("strong", { children: [a.month, " \u00B7 ", a.title] }), _jsx(CashFacts, { rows: [
                                                        [
                                                            a.originalAmountKnown
                                                                ? t.accrued
                                                                : {
                                                                    az: 'Məlum başlanğıc qalıq',
                                                                    ru: 'Известный начальный остаток',
                                                                    en: 'Known opening remainder',
                                                                }[p.locale],
                                                            money(a.amountMinor),
                                                        ],
                                                        [t.allocated, money(a.paidMinor)],
                                                        [t.remaining, money(a.remainingMinor)],
                                                    ] }), a.state === 'ARCHIVED' ? (_jsx("p", { className: "cash-muted", children: { az: 'Arxivdə', ru: 'В архиве', en: 'Archived' }[p.locale] })) : (a.remainingMinor === '0' && _jsx("p", { className: "cash-muted", children: t.paid }))] }, a.basisId))), !forecast &&
                                        !c.confirmedPeriods &&
                                        periods.map(a => (_jsxs("p", { children: [a.month, " \u00B7 ", a.title, " \u00B7 ", money(a.remainingMinor), a.dueDate
                                                    ? ` · ${a.dueDate < p.businessDate ? t.overdue : t.due}: ${a.dueDate}`
                                                    : ''] }, a.basisId))), forecast &&
                                        forecasts.map(b => (_jsxs("div", { className: "cash-section", children: [_jsxs("p", { children: [b.month, " \u00B7 ", b.title, " \u00B7 ", money(b.amountMinor)] }), forecastCount > 1 &&
                                                    b.actions.includes('RECURRING_FORECAST_FINALIZE') &&
                                                    p.onFinalize && (_jsxs(CashButton, { disabled: p.busy, onClick: () => p.onFinalize?.(b.id), children: [t.finalize, " \u00B7 ", b.month] }))] }, b.id))), !forecast && c.accrualIncomplete && (_jsx(CashNotice, { children: {
                                            az: 'İlkin xərcin tam məbləği məlum deyil; borc qalığı məlumdur',
                                            ru: 'Полная первоначальная сумма расхода неизвестна; остаток долга известен',
                                            en: 'The full original expense is unknown; the debt remainder is known',
                                        }[p.locale] })), !forecast &&
                                        (p.bases ?? [])
                                            .filter(b => !b.forecast &&
                                            !b.coverageKnown &&
                                            (c.employeeId
                                                ? b.employeeId === c.employeeId
                                                : b.recurringRuleId === c.recurringRuleId) &&
                                            b.storeId === c.storeId &&
                                            (b.accountId ?? '') === (c.accountId ?? '') &&
                                            b.currency === c.currency &&
                                            (!c.month || b.month <= c.month))
                                            .map(b => (_jsxs("div", { children: [_jsxs("p", { children: [b.month, " \u00B7 ", b.title, " \u00B7 ", t.unknown] }), b.actions.includes('PAYABLE_CONFIRM') && p.onReviewBasis && (_jsx(CashButton, { disabled: p.busy, onClick: () => p.onReviewBasis?.(b.id), children: {
                                                        az: 'Tarixçəni yoxla',
                                                        ru: 'Проверить историю',
                                                        en: 'Review history',
                                                    }[p.locale] }))] }, b.id))), !forecast &&
                                        c.advances.map(a => (_jsxs("div", { className: "cash-section", children: [_jsxs("strong", { children: [t.advance, " \u00B7 ", a.month, ": ", money(a.availableMinor)] }), a.canRetarget && BigInt(a.availableMinor) > 0n && (_jsx(CashButton, { disabled: p.busy, onClick: () => setSelected({
                                                        key: c.key,
                                                        kind: 'retarget',
                                                        advanceId: a.advanceId,
                                                        month: a.month,
                                                    }), children: t.retarget }))] }, a.advanceId + ':' + a.month))), !forecast &&
                                        c.employeeId &&
                                        c.canPay &&
                                        periods
                                            .filter(a => a.month === c.month)
                                            .map(a => (_jsxs(CashButton, { disabled: p.busy, onClick: () => p.onPay({ mode: 'BASIS', basisId: a.basisId, salaryPaymentType: 'ADVANCE' }, c.categoryId, c.currency, {
                                                ...paymentContext(c),
                                                month: a.month,
                                                remainingMinor: a.remainingMinor,
                                                advance: true,
                                            }), children: [t.salaryAdvance, " ", a.month] }, a.basisId))), !forecast && c.canAdvance && (_jsx(CashButton, { disabled: p.busy, onClick: () => setSelected({ key: c.key, kind: 'future' }), children: t.futureOnly }))] })] }, c.key));
                }), selected && card && allowed && (_jsxs(CashDialog, { title: `${card.title} · ${selected.kind === 'retarget' ? t.retarget : selected.kind === 'future' ? t.futureOnly : t.periods}`, closeLabel: t.cancel, onClose: close, children: [p.error && _jsx(CashNotice, { tone: "danger", children: p.error }), p.recoveryAction, _jsx("fieldset", { className: "cash-command-fields", disabled: p.busy, children: selected.kind !== 'retarget' ? (_jsx(PeriodAllocation, { ...p, card: card, futureOnly: selected.kind === 'future', onPay: (...args) => {
                                setSelected(null);
                                p.onDirty(false);
                                p.onFutureClose?.();
                                p.onPay(...args);
                            }, onClose: close }, card.key + selected.kind)) : (advance && (_jsx(RetargetForm, { ...p, card: card, advance: advance, onClose: close, onSaved: () => {
                                setSelected(null);
                                p.onDirty(false);
                            } }, advance.advanceId + advance.month))) })] }))] }));
}
function PeriodAllocation(p) {
    const { card } = p, t = labels[p.locale], money = (n) => formatCashMinor(n, card.currency, p.locale);
    const [amounts, setAmounts] = useState(() => Object.fromEntries((p.futureOnly ? [] : cardPeriods(card)).map(a => [
        a.basisId,
        cashAmountInput(a.remainingMinor, card.currency),
    ])));
    const [future, setFuture] = useState(Boolean(p.futureOnly)), [futureAmount, setFutureAmount] = useState(''), [month, setMonth] = useState(() => {
        const [y, m] = p.businessDate.slice(0, 7).split('-').map(Number);
        return `${y + (m === 12 ? 1 : 0)}-${String(m === 12 ? 1 : m + 1).padStart(2, '0')}`;
    });
    const allocations = Object.entries(amounts)
        .filter(([, amount]) => Boolean(amount.trim()))
        .map(([basisId, amount]) => ({
        basisId,
        amountMinor: parseCashAmount(amount, false, card.currency),
    }));
    const futureMinor = future ? parseCashAmount(futureAmount, false, card.currency) : '0';
    const missing = allocations.some(a => !card.periods.some(b => b.basisId === a.basisId));
    const valid = !missing &&
        allocations.every(a => a.amountMinor !== null &&
            BigInt(a.amountMinor) <=
                BigInt(card.periods.find(b => b.basisId === a.basisId).remainingMinor)) &&
        futureMinor !== null &&
        (!future ||
            (card.canAdvance && cashPeriodValid(month) && month > p.businessDate.slice(0, 7))) &&
        (allocations.length > 0 || future);
    const total = allocations.reduce((sum, a) => sum + BigInt(a.amountMinor ?? '0'), 0n) +
        BigInt(futureMinor ?? '0');
    const state = JSON.stringify([amounts, month, futureAmount, future]), [baseline] = useState(state);
    useEffect(() => {
        p.onDirty(state !== baseline);
    }, [state, baseline, p.onDirty]);
    return (_jsxs("form", { onSubmit: e => {
            e.preventDefault();
            if (!valid || p.busy || total <= 0n)
                return;
            p.onDirty(false);
            p.onPay({
                mode: 'BASIS',
                periods: {
                    ...(card.employeeId
                        ? { employeeId: card.employeeId }
                        : { recurringRuleId: card.recurringRuleId }),
                    allocations: allocations.map(a => ({
                        basisId: a.basisId,
                        amountMinor: a.amountMinor,
                    })),
                    ...(future ? { advance: { month, amountMinor: futureMinor } } : {}),
                },
            }, card.categoryId, card.currency, {
                ...paymentContext(card),
                ...(p.futureOnly
                    ? { advance: true, purpose: `${card.title} · ${t.futureOnly} · ${month}`, month }
                    : {}),
            });
        }, children: [missing && _jsx(CashNotice, { tone: "warn", children: t.changed }), _jsx("div", { className: "cash-stack", children: (p.futureOnly ? [] : cardPeriods(card)).map(a => (_jsxs("div", { className: "cash-section", children: [_jsxs("label", { className: "cash-check", children: [_jsx("input", { type: "checkbox", checked: a.basisId in amounts, onChange: e => setAmounts(before => {
                                        const next = { ...before };
                                        if (e.target.checked)
                                            next[a.basisId] = cashAmountInput(a.remainingMinor, card.currency);
                                        else
                                            delete next[a.basisId];
                                        return next;
                                    }) }), a.month, " \u00B7 ", a.title, " \u00B7 ", money(a.remainingMinor)] }), _jsx("small", { children: a.dueDate
                                ? `${a.dueDate < p.businessDate ? t.overdue : t.due}: ${a.dueDate}`
                                : t.noDue }), a.basisId in amounts && (_jsx(CashField, { label: `${t.amount} · ${a.month}`, children: _jsx("input", { inputMode: "decimal", value: amounts[a.basisId], onChange: e => setAmounts(before => ({ ...before, [a.basisId]: e.target.value })) }) }))] }, a.basisId))) }), card.canAdvance && !p.futureOnly && (_jsxs("label", { className: "cash-check", children: [_jsx("input", { type: "checkbox", checked: future, onChange: e => setFuture(e.target.checked) }), t.future] })), future && (_jsxs("div", { className: "cash-fields", children: [_jsx(CashPeriodPicker, { label: t.month, locale: p.locale, value: month, onChange: setMonth }), _jsx(CashField, { label: `${t.amount} · ${card.currency}`, children: _jsx("input", { inputMode: "decimal", value: futureAmount, onChange: e => setFutureAmount(e.target.value) }) }), _jsx(CashNotice, { children: t.approval })] })), _jsx(CashFacts, { rows: [[t.total, money(total.toString())]] }), _jsxs("div", { className: "cash-footer", children: [_jsx(CashButton, { disabled: p.busy, onClick: p.onClose, children: t.cancel }), _jsx(CashButton, { primary: true, type: "submit", disabled: p.busy || !valid || total <= 0n, children: t.next })] })] }));
}
function RetargetForm(p) {
    const t = labels[p.locale], [month, setMonth] = useState(''), [amount, setAmount] = useState(cashAmountInput(p.advance.availableMinor, p.card.currency)), [reason, setReason] = useState(''), [available] = useState(p.advance.availableMinor);
    const minor = parseCashAmount(amount, false, p.card.currency), valid = minor !== null &&
        BigInt(minor) <= BigInt(available) &&
        cashPeriodValid(month) &&
        month > p.businessDate.slice(0, 7) &&
        month !== p.advance.month &&
        reason.trim();
    const state = JSON.stringify([month, amount, reason]), [baseline] = useState(state);
    useEffect(() => p.onDirty(state !== baseline), [state, baseline, p.onDirty]);
    return (_jsxs("form", { onSubmit: async (e) => {
            e.preventDefault();
            if (!valid || p.busy)
                return;
            const saved = await p.submit('TARGETED_ADVANCE_RETARGET', {
                advanceId: p.advance.advanceId,
                fromMonth: p.advance.month,
                toMonth: month,
                amountMinor: minor,
                expectedAvailableMinor: available,
                reason: reason.trim(),
            });
            if (saved) {
                p.onDirty(false);
                await p.onReload();
                p.onSaved();
            }
        }, children: [_jsx(CashFacts, { rows: [
                    [
                        t.advance,
                        `${p.advance.month} · ${formatCashMinor(available, p.card.currency, p.locale)}`,
                    ],
                ] }), _jsx(CashPeriodPicker, { label: t.month, locale: p.locale, value: month, onChange: setMonth }), _jsx(CashField, { label: t.amount, children: _jsx("input", { inputMode: "decimal", value: amount, onChange: e => setAmount(e.target.value) }) }), _jsx(CashField, { label: t.reason, children: _jsx("textarea", { value: reason, onChange: e => setReason(e.target.value) }) }), _jsxs("div", { className: "cash-footer", children: [_jsx(CashButton, { onClick: p.onClose, children: t.cancel }), _jsx(CashButton, { primary: true, type: "submit", disabled: p.busy || !valid, children: t.save })] })] }));
}
